import { useEffect, useRef, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { IDLE_TIMEOUT } from "../../utils/constants";
import toast from "react-hot-toast";

interface UseCustomIdleTimerProps {
  onIdle: () => void;
  isLoggedIn: boolean;
  warningTime?: number; // Time before logout to show warning (default: 2 minutes)
  onStateChange?: (isActive: boolean) => void; // Callback for state changes
}

export const useCustomIdleTimer = ({
  onIdle,
  isLoggedIn,
  warningTime = 2 * 60 * 1000, // 2 minutes
  onStateChange,
}: UseCustomIdleTimerProps) => {
  const idleTimerRef = useRef<any>(null);
  const warningShownRef = useRef(false);
  const [isUserActive, setIsUserActive] = useState(true);

  const handleIdle = () => {
    setIsUserActive(false);
    onStateChange?.(false);
    if (isLoggedIn) {
      onIdle();
    }
  };

  const handleActive = () => {
    setIsUserActive(true);
    onStateChange?.(true);
    // Reset the timer when user becomes active
    if (idleTimerRef.current) {
      idleTimerRef.current.reset();
    }
    // Hide warning if it was shown
    if (warningShownRef.current) {
      warningShownRef.current = false;
      toast.dismiss("idle-warning");
    }
  };

  const handleAction = () => {
    setIsUserActive(true);
    onStateChange?.(true);
    // Reset the timer on any user action
    if (idleTimerRef.current) {
      idleTimerRef.current.reset();
    }
    // Hide warning if it was shown
    if (warningShownRef.current) {
      warningShownRef.current = false;
      toast.dismiss("idle-warning");
    }
  };

  const handlePrompt = () => {
    if (isLoggedIn && !warningShownRef.current) {
      warningShownRef.current = true;
      toast.error(
        "Your session will expire soon due to inactivity. Please continue working to stay logged in.",
        {
          id: "idle-warning",
          duration: 30000, // 30 seconds
          position: "top-right",
        },
      );
    }
  };

  const idleTimer = useIdleTimer({
    timeout: IDLE_TIMEOUT,
    onIdle: handleIdle,
    onActive: handleActive,
    onAction: handleAction,
    onPrompt: handlePrompt,
    debounce: 500,
    events: [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "focus",
      "blur",
      "wheel",
      "contextmenu",
      "select",
      "input",
      "change",
    ],
    startOnMount: isLoggedIn,
    startManually: !isLoggedIn,
    promptBeforeIdle: warningTime,
  });

  useEffect(() => {
    idleTimerRef.current = idleTimer;
  }, [idleTimer, isLoggedIn]);

  useEffect(() => {
    if (isLoggedIn) {
      idleTimer.start();
    } else {
      idleTimer.pause();
      // Clear any existing warnings when logging out
      if (warningShownRef.current) {
        warningShownRef.current = false;
        toast.dismiss("idle-warning");
      }
    }
  }, [isLoggedIn, idleTimer]);

  // Add a method to get the current active state
  const getIsActive = () => isUserActive;

  return {
    ...idleTimer,
    getIsActive,
  };
};
